import React, {createContext, useContext, useMemo, useReducer} from 'react';
import {ActionType} from 'typesafe-actions';
import {UserFeedbackActionTypes} from './UserFeedbackActionTypes';
import {Feedback} from '../../../types';

export type UserFeedbackAction = ActionType<typeof import('./UserFeedbackActionTypes')>;

export type UserFeedbackState = {
  title: string;
  children?: any;
} & Feedback;

const userFeedbackReducer = (
  state: UserFeedbackState,
  action: UserFeedbackAction
): UserFeedbackState => {
  switch (action.type) {
    case UserFeedbackActionTypes.SET_ERROR_FEEDBACK: {
      return {
        ...state,
        open: true,
        type: 'error',
        ...action.payload,
      };
    }
    case UserFeedbackActionTypes.SET_INFO_FEEDBACK: {
      return {
        ...state,
        open: true,
        type: 'info',
        ...action.payload,
      };
    }
    case UserFeedbackActionTypes.SET_WARNING_FEEDBACK: {
      return {
        ...state,
        open: true,
        type: 'warning',
        ...action.payload,
      };
    }
    case UserFeedbackActionTypes.CLOSE_FEEDBACK: {
      return {
        ...state,
        open: false,
        title: '',
        message: '',
        showHeader: true,
      };
    }
    default: {
      return state;
    }
  }
};

export type UserFeedbackContextState = {
  state: UserFeedbackState;
  dispatch: React.Dispatch<UserFeedbackAction>;
};

export const UserFeedbackContext = createContext({} as UserFeedbackContextState);

const UserFeedbackProvider = ({children}: {children: any}) => {
  const [state, dispatch] = useReducer(userFeedbackReducer, {
    open: false,
    type: 'info',
    title: '',
    message: '',
    showHeader: true,
  });

  const contextValue = useMemo(
    () => ({
      state,
      dispatch,
    }),
    [state, dispatch]
  );

  return (
    <UserFeedbackContext.Provider value={contextValue}>{children}</UserFeedbackContext.Provider>
  );
};

const useUserFeedback = () => {
  const context = useContext(UserFeedbackContext);
  if (undefined === context) {
    throw new Error('useUserFeedback must be used within UserFeedbackProvider');
  }
  return context;
};

export {UserFeedbackProvider, useUserFeedback};
